import { Category } from './categories';
import { API_BASE_URL } from './apiBase';

// Get all categories from server with retry for spindown
export const getCategoriesFromServer = async (retryCount = 0): Promise<Category[]> => {
  try {
    console.log(`📱 Fetching categories from server... (attempt ${retryCount + 1}) - v1.0`);
    // Add cache-busting parameter to ensure fresh data
    const cacheBuster = Date.now();
    const response = await fetch(`${API_BASE_URL}/categories?_cb=${cacheBuster}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    
    if (!response.ok) {
      if (response.status === 404 && retryCount < 2) {
        console.log('🔄 Server might be spinning up, retrying in 5 seconds...');
        await new Promise(resolve => setTimeout(resolve, 5000));
        return getCategoriesFromServer(retryCount + 1);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const categories = await response.json();
    console.log('📱 Server returned categories:', categories.length);
    
    // Ensure categories have proper structure
    const formattedCategories = categories.map((category: any) => ({
      id: category.id,
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      color: category.color || '#8B4513',
      createdAt: category.createdAt || new Date().toISOString(),
      updatedAt: category.updatedAt || new Date().toISOString()
    }));
    
    console.log('📱 Formatted server categories:', formattedCategories.length);
    return formattedCategories;
  } catch (error) {
    console.error('Error fetching categories from server:', error);
    return [];
  }
};

// Save categories to server
export const saveCategoriesToServer = async (categories: Category[]): Promise<boolean> => {
  try {
    console.log('📱 Saving categories to server:', categories.length);
    const response = await fetch(`${API_BASE_URL}/categories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(categories),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('📱 Categories saved to server:', result);
    return result.success;
  } catch (error) {
    console.error('Error saving categories to server:', error);
    return false;
  }
};

// Rename a category globally (updates galleries too)
export const renameCategoryOnServer = async (
  oldSlug: string,
  newSlug: string,
  newName?: string,
  newColor?: string
): Promise<{ success: boolean; updatedCategories?: number; updatedGalleries?: number }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/categories/rename`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ oldSlug, newSlug, newName, newColor }),
    });
    if (!response.ok) {
      return { success: false };
    }
    return await response.json();
  } catch (e) {
    console.error('Error renaming category on server:', e);
    return { success: false };
  }
};

// Load categories - LIVE SERVER ONLY in production
export const loadLiveCategories = async (): Promise<Category[]> => {
  try {
    console.log('🌐 LIVE MODE: Loading categories from server only...');
    
    // In production, ONLY use server - no localStorage fallbacks
    const serverCategories = await getCategoriesFromServer();
    
    if (serverCategories.length > 0) {
      console.log('✅ LIVE: Using server categories:', serverCategories.length);
      return serverCategories;
    }
    
    // Server has no categories - return empty array (don't fallback to localStorage)
    console.log('📭 LIVE: Server has no categories - returning empty array');
    return [];
    
  } catch (error) {
    console.error('❌ LIVE: Error loading categories from server:', error);
    // In production, don't fallback to localStorage or samples
    return [];
  }
};
